import { useTranslation } from "react-i18next";
import { Image } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { OFFER_TYPES } from "../../../../../constants";
import { formatNumber, getCurrencySymbolFromCache } from "../../../../../utils";
import {
  deliveryCostAmount,
  effectiveDeliveryCost,
  hasDeliveryCost,
} from "../../../../../utils/delivery-cost";
import { getLocalisedName } from "../../../../../utils/i18n";
import { formatDateTimeCompact } from "../../../../../utils/relative-time";
import {
  activeProposalTerms,
  formatTotalPrice,
  formatUnitPrice,
  hasDepositRound,
  isDeliveryRound,
  offerAwaitingParty,
  offerDeliveryNoteUrl,
  offerOriginalSource,
  offerStatusBadgeVariant,
  offerStatusLabelKey,
} from "../../../utils";
import { Offer } from "../../OfferCard/interfaces";
import { NegotiationProposal } from "../NegotiatedDetailsCard/offer-negotiation-proposal";

interface OfferDetailRailProps {
  offer: Offer;
  proposals: NegotiationProposal[];
  round: number;
  onRoundChange: (round: number) => void;
  readOnly?: boolean;
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
    {children}
  </span>
);

const RoundButton = ({
  label,
  value,
  selected,
  onSelect,
}: {
  label: string;
  value: string;
  selected: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-current={selected}
    className={cn(
      "flex shrink-0 cursor-pointer flex-col gap-0.5 rounded-md border border-border px-3 py-2 text-left text-sm md:w-full md:shrink md:flex-row md:justify-between md:gap-2 md:rounded-none md:border-0 md:border-l-2",
      selected
        ? "border-primary bg-info-50 font-semibold text-info-700 md:border-l-primary"
        : "text-muted-foreground md:border-l-border"
    )}
  >
    <span>{label}</span>
    <span className="text-foreground">{value}</span>
  </button>
);

export const OfferDetailRail = ({
  offer,
  proposals,
  round,
  onRoundChange,
  readOnly = false,
}: OfferDetailRailProps) => {
  const { t } = useTranslation();

  const unit = offer.unit_of_measure?.code;
  const currency = getCurrencySymbolFromCache() || offer.currency?.symbol;

  const awaiting = offerAwaitingParty(offer);
  const status = t(offerStatusLabelKey(offer.status));
  const statusLabel =
    awaiting === "you" && !readOnly ? t("offerStatuses.awaitingYou", { status }) : status;

  const deposit =
    offer.offer_type === OFFER_TYPES.DIRECT_OFFER && offer.deposit ? offer.deposit : undefined;
  const depositRound = hasDepositRound(offer);
  const deliveryNoteUrl = offerDeliveryNoteUrl(offer);
  const deliveryCost = (activeProposalTerms(offer) ?? offer).delivery_cost;

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto border-border border-b bg-muted/40 p-6 md:gap-5 md:border-r md:border-b-0">
      <div className="flex flex-col gap-2">
        <span className="font-semibold text-foreground text-xl">
          {getLocalisedName(offer.buy_commodity)} ({offer.quality})
        </span>
        <SectionLabel>
          {t(
            offer.offer_type === OFFER_TYPES.MARKET_OFFER
              ? "marketOfferView.marketOffer"
              : "marketOfferView.directOffer"
          )}{" "}
          #{offer.id}
        </SectionLabel>
        <div>
          <Badge
            variant={offerStatusBadgeVariant(offer)}
            className="h-auto whitespace-normal text-left"
          >
            {statusLabel}
          </Badge>
        </div>
      </div>

      {offer.tagged_aggregator?.name && (
        <div className="flex flex-col gap-1">
          <SectionLabel>{t("offerDetail.aggregator")}</SectionLabel>
          <span className="text-foreground text-sm">{offer.tagged_aggregator.name}</span>
        </div>
      )}

      {hasDeliveryCost(deliveryCost) && (
        <div className="flex flex-col gap-1">
          <SectionLabel>{t("offerDetail.deliveryCost")}</SectionLabel>
          <span className="text-foreground text-sm">
            {`${currency}${formatNumber(deliveryCostAmount(deliveryCost))}`}
          </span>
        </div>
      )}

      {deposit && (
        <div className="flex flex-col gap-1">
          <SectionLabel>{t("offerDetail.deposit")}</SectionLabel>
          <span className="text-foreground text-sm">
            {t("offerDetail.depositTerms", {
              quantity: formatNumber(deposit.original_quantity ?? deposit.quantity),
              unit,
              price: formatUnitPrice(deposit.price, currency, unit),
            })}
          </span>
        </div>
      )}

      {deliveryNoteUrl && (
        <div className="flex flex-col gap-1">
          <SectionLabel>{t("offerDetail.deliveryNote")}</SectionLabel>
          <div>
            <WFPButton variant="outline" size="sm" asChild>
              <a href={deliveryNoteUrl} target="_blank" rel="noopener noreferrer">
                <Image className="size-4" />
                {t("offerDetail.viewDeliveryNote")}
              </a>
            </WFPButton>
          </div>
        </div>
      )}

      {proposals.length > 0 && (
        <div className="flex flex-col gap-2">
          <SectionLabel>{t("offerDetail.negotiation")}</SectionLabel>
          <div className="flex gap-2 overflow-x-auto md:flex-col md:gap-0 md:overflow-visible">
            {depositRound && (
              <RoundButton
                label={t("offerDetail.roundOffer", { round: 1 })}
                value={formatTotalPrice(offerOriginalSource(offer), currency)}
                selected={round < 0}
                onSelect={() => onRoundChange(-1)}
              />
            )}
            {proposals.map((proposal, index) => (
              <RoundButton
                key={proposal.uuid}
                label={t(
                  isDeliveryRound(offer, index, proposals.length)
                    ? "offerDetail.roundDelivery"
                    : "offerDetail.roundCounter",
                  { round: index + (depositRound ? 2 : 1) }
                )}
                value={formatTotalPrice(
                  { ...proposal, delivery_cost: effectiveDeliveryCost(proposal, offer) },
                  currency
                )}
                selected={index === round}
                onSelect={() => onRoundChange(index)}
              />
            ))}
          </div>
        </div>
      )}

      {offer.valid_until && (
        <span className="text-muted-foreground text-xs">
          {t("offerDetail.validUntil", { date: formatDateTimeCompact(offer.valid_until) })}
        </span>
      )}
    </div>
  );
};
